/**
 * AuthContext + useAuth — single source of truth for the current Supabase
 * user/session in the React tree.
 *
 * UI files MUST consume auth state via `useAuth()` and never call
 * `supabase.auth.*` directly. All side-effects are routed through
 * `@/services/auth`.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';

import {
  getCurrentSession,
  signInWithPassword,
  signOut as serviceSignOut,
  signUpWithEmailPassword,
  subscribeToAuthChanges,
} from '@/services/auth';
import { claimPendingReferral, claimPendingBuddyInvite } from '@/services/referrals';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
  ) => ReturnType<typeof signUpWithEmailPassword>;
  signIn: (
    email: string,
    password: string,
  ) => ReturnType<typeof signInWithPassword>;
  signOut: () => ReturnType<typeof serviceSignOut>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    // Track which users we've already self-healed in this session to avoid
    // hammering the sync RPC on every TOKEN_REFRESHED tick.
    const healedUserIds = new Set<string>();

    /**
     * Forced post-verification refresh.
     *
     * When auth fires USER_UPDATED (the event Supabase emits after email
     * confirmation) or the very first SIGNED_IN with a confirmed email,
     * we MUST drop every cached identity-derived state — otherwise the
     * admin user-list, the BetaGate decision and the verification badge
     * keep showing the pre-confirmation snapshot until next reload.
     *
     * As belt-and-suspenders we also call the self-heal RPC once per
     * session so the profile row is guaranteed to reflect auth.users
     * even if the DB trigger ever lagged.
     */
    const forcePostVerificationRefresh = async (userId: string) => {
      if (healedUserIds.has(userId)) return;
      healedUserIds.add(userId);
      try {
        await supabase.rpc('sync_profile_verification_for_user', {
          _user_id: userId,
        });
      } catch {
        // Non-fatal — invalidation alone will surface fresh data.
      }
      // Drop every cache entry that depends on verification_status.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['effective-access'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-profiles'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-user-profile'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-auth-email-confirmed'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-user-audit'] }),
        queryClient.invalidateQueries({ queryKey: ['user-roles'] }),
      ]);
    };

    // Subscribe FIRST to avoid race conditions, then hydrate from storage.
    const { subscription } = subscribeToAuthChanges((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });

      // Forced refresh on email-confirmation events.
      // - USER_UPDATED: fires when email_confirmed_at transitions in auth.users
      //   (i.e. user just clicked the verification link).
      // - SIGNED_IN with email_confirmed_at: covers the case where a user
      //   verifies in another tab and the session is rehydrated here.
      const u = nextSession?.user;
      if (u && (event === 'USER_UPDATED' || event === 'SIGNED_IN') && u.email_confirmed_at) {
        setTimeout(() => {
          void forcePostVerificationRefresh(u.id);
        }, 0);
      }

      // Best-effort: claim any pending referral code now that we have a session.
      // Defer to next tick so the session token is fully propagated to the client.
      if (nextSession?.user) {
        setTimeout(() => {
          void claimPendingReferral();
          void claimPendingBuddyInvite();
        }, 0);
      }
    });

    getCurrentSession().then((current) => {
      if (cancelled) return;
      setSession(current);
      setUser(current?.user ?? null);
      setLoading(false);
      // Cold-boot path: if a user lands on the app already confirmed, run
      // the same forced refresh once so any stale local cache is dropped.
      if (current?.user?.email_confirmed_at) {
        setTimeout(() => {
          void forcePostVerificationRefresh(current.user!.id);
        }, 0);
      }
      if (current?.user) {
        setTimeout(() => {
          void claimPendingReferral();
          void claimPendingBuddyInvite();
        }, 0);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      signUp: (email, password) =>
        signUpWithEmailPassword(email, password, window.location.origin),
      signIn: (email, password) => signInWithPassword(email, password),
      signOut: () => serviceSignOut(),
    }),
    [user, session, loading],
  );

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
